/**
 * terrain_stamp.ts — the Commons terrain CONFORMS to settlement roads and
 * building pads (the last city-placement research finding: Galin-style
 * road/terrain coupling).
 *
 * The engine half always existed: plans grade every road edge against the real
 * terrain (RoadGrader → edge.gradeProfile, world heights) and RoadMeshBuilder
 * drapes the asphalt on that profile. What never happened is the ground
 * conforming BACK — so roads bridged dips and sliced through rises. This lib is
 * the point-query mirror of the engine's grid TerrainStamper, applied wherever
 * atlas samples ground height:
 *
 *   stampedHeight(seed, wx, wz)
 *     = terrainHeight(...) corridor-blended toward the graded road profile
 *       within [halfWidth, halfWidth + roadFalloff] (+ crown), then toward each
 *       building pad's padHeight within its bevel — sequential compose, same
 *       math as TerrainStamper.stamp so meshes and ground agree exactly.
 *
 * Consumers: chunk geometry (→ trimesh colliders follow automatically),
 * terrainSeat (buildings/props/foliage), and Versona Y — everything that stood
 * on terrainHeight now stands on the conformed ground.
 *
 * Cost control: a per-point site reject (footprint + falloff + margin) keeps
 * wilderness samples at one distance check; plans are memoized in
 * settlement_plan, and precacheSettlementPlans() warms them one-per-tick after
 * world entry so chunk builds never hitch on first approach.
 */

import { terrainHeight } from '../../../shared/terrain.js';
import { applySculpt } from './terrain_edits.js';
import { settlementSites, type SettlementSite } from '../../../shared/settlements.js';
import { distanceToPolyline, signedDistanceToPolygon } from 'enginex/city/terrain/SDFUtils.js';
import type { CityPlan } from 'enginex/city/config.js';
import { peekSettlementPlan, PLAN_RADIUS_DESIRED } from './settlement_plan.js';
import { portAprons, portClearances } from './ports.js';
import { routeMask } from './regional_routes.js';
import { circuitHeight, circuitMask } from './race_circuit.js';
import { desireLinesFor } from './districts.js';

/** Reach beyond the plan radius: road falloff (8) + pad bevel (5) + slack. */
const STAMP_MARGIN = 20;

const sitesCache = new Map<string, SettlementSite[]>();
const warmed = new Set<string>();

function sitesFor(seed: string): SettlementSite[] {
  let s = sitesCache.get(seed);
  if (!s) {
    s = settlementSites(seed);
    sitesCache.set(seed, s);
  }
  return s;
}

/** gradeProfile is sampled at 2 m arc intervals (RoadGrader SAMPLE_STEP). */
function sampleProfile(profile: Float32Array, arc: number): number {
  if (profile.length === 0) return 0;
  const f = arc / 2;
  const i0 = Math.max(0, Math.min(profile.length - 1, Math.floor(f)));
  const i1 = Math.min(profile.length - 1, i0 + 1);
  const t = Math.max(0, Math.min(1, f - i0));
  return profile[i0] + (profile[i1] - profile[i0]) * t;
}

function smoothstep(e0: number, e1: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}

/**
 * Per-plan SPATIAL GRID over road edges + pads (2026-07-20 overhaul): plans
 * grew from ~70 to ~630 shapes, and the naive per-point scan made a chunk near
 * a city cost MILLIONS of distance evals (seconds per chunk — the terrain
 * visibly failed to stream). Cells hold the indices of every shape whose
 * bbox (+ its blend reach) touches them; a query then evaluates only the
 * handful of local shapes — bbox supersets keep the math byte-identical.
 */
interface StampIndex {
  cell: number; minX: number; minZ: number; cols: number; rows: number;
  edges: Int32Array[]; pads: Int32Array[];
}
const indexCache = new WeakMap<CityPlan, StampIndex>();

function indexFor(plan: CityPlan): StampIndex {
  let idx = indexCache.get(plan);
  if (idx) return idx;
  const falloff = plan.config.terrain.roadFalloff;
  const bevel = plan.config.terrain.padBevelWidth;
  const R = plan.config.radius + STAMP_MARGIN + 8;
  const cell = 32;
  const cols = Math.max(1, Math.ceil((2 * R) / cell));
  const eLists: number[][] = Array.from({ length: cols * cols }, () => []);
  const pLists: number[][] = Array.from({ length: cols * cols }, () => []);
  const stampBBox = (lists: number[][], i: number, x0: number, z0: number, x1: number, z1: number): void => {
    const c0 = Math.max(0, Math.floor((x0 + R) / cell));
    const c1 = Math.min(cols - 1, Math.floor((x1 + R) / cell));
    const r0 = Math.max(0, Math.floor((z0 + R) / cell));
    const r1 = Math.min(cols - 1, Math.floor((z1 + R) / cell));
    for (let r = r0; r <= r1; r++) for (let c = c0; c <= c1; c++) lists[r * cols + c].push(i);
  };
  for (let i = 0; i < plan.roads.edges.length; i++) {
    const e = plan.roads.edges[i];
    const reach = e.halfWidth + falloff;
    let x0 = Infinity, z0 = Infinity, x1 = -Infinity, z1 = -Infinity;
    for (let k = 0; k < e.polyline.length; k += 2) {
      if (e.polyline[k] < x0) x0 = e.polyline[k];
      if (e.polyline[k] > x1) x1 = e.polyline[k];
      if (e.polyline[k + 1] < z0) z0 = e.polyline[k + 1];
      if (e.polyline[k + 1] > z1) z1 = e.polyline[k + 1];
    }
    stampBBox(eLists, i, x0 - reach, z0 - reach, x1 + reach, z1 + reach);
  }
  for (let i = 0; i < plan.buildings.length; i++) {
    const f = plan.buildings[i].footprint;
    let x0 = Infinity, z0 = Infinity, x1 = -Infinity, z1 = -Infinity;
    for (let k = 0; k < f.length; k += 2) {
      if (f[k] < x0) x0 = f[k];
      if (f[k] > x1) x1 = f[k];
      if (f[k + 1] < z0) z0 = f[k + 1];
      if (f[k + 1] > z1) z1 = f[k + 1];
    }
    stampBBox(pLists, i, x0 - bevel, z0 - bevel, x1 + bevel, z1 + bevel);
  }
  idx = {
    cell, minX: -R, minZ: -R, cols, rows: cols,
    edges: eLists.map((l) => Int32Array.from(l)),
    pads: pLists.map((l) => Int32Array.from(l)),
  };
  indexCache.set(plan, idx);
  return idx;
}

/** One site's stamp — sequential road-then-pad compose (TerrainStamper parity),
 *  evaluated over the spatial-grid cell's shapes only. */
function stampSite(h: number, lx: number, lz: number, plan: CityPlan): number {
  const falloff = plan.config.terrain.roadFalloff;
  const bevel = plan.config.terrain.padBevelWidth;
  const crown = plan.config.roads.crownHeight;
  const idx = indexFor(plan);
  const c = Math.floor((lx - idx.minX) / idx.cell);
  const r = Math.floor((lz - idx.minZ) / idx.cell);
  if (c < 0 || r < 0 || c >= idx.cols || r >= idx.rows) return h;
  const cellI = r * idx.cols + c;
  for (const ei of idx.edges[cellI]) {
    const edge = plan.roads.edges[ei];
    const d = distanceToPolyline(lx, lz, edge.polyline);
    const outer = edge.halfWidth + falloff;
    if (d.dist < outer) {
      const mask = 1 - smoothstep(edge.halfWidth, outer, d.dist);
      const roadH = sampleProfile(edge.gradeProfile, d.arcLength) + crown;
      h = h + (roadH - h) * mask;
    }
  }
  for (const pi of idx.pads[cellI]) {
    const pad = plan.buildings[pi];
    const sd = signedDistanceToPolygon(lx, lz, pad.footprint);
    if (sd < bevel) {
      const mask = 1 - smoothstep(0, bevel, sd);
      h = h + (pad.padHeight - h) * mask;
    }
  }
  return h;
}

// ── Building stamps (interiors wave 2026-08-14): the plan stamp flattens each
// pad's PLAN polygon, but the lattice wave scales buildings far beyond their
// pads — the slope ran straight through big towers' interiors (visible wall
// of dirt + a terrain trimesh collider wedging climbers mid-stairs). Each
// ENTERABLE building registers its ACTUAL scaled rectangle at assembly; the
// terrain flattens to the building's ground height inside it (mesh AND
// collider follow — chunks rebuild from stampedHeight). Deterministic per
// session: same assembly ⇒ same stamps. ──
export interface BuildingStamp {
  x: number; z: number; hw: number; hd: number; yaw: number; h: number;
  /** THE ROOM FIT (interiors programme C2, 2026-10-01) — a CUT-ONLY margin (m) beyond the rect: ground HIGHER than `h`
   *  is cut down to it out to the margin (then the bevel); ground at or under `h` is never touched, nothing is ever
   *  raised. Absent = today's stamp, bit for bit. See `roomFitMargin`. */
  fit?: number;
}
// The registry lives on globalThis: identical import specifiers were observed
// resolving to distinct module instances in the dev graph (app registry held
// stamps while a sibling module's instance read zero) — a shared store makes
// the registry instance-proof. The chunk WORKER has its own globalThis and is
// fed stamps via the job payload (setBuildingStamps), unchanged.
const G = globalThis as unknown as { __atlasBStamps?: BuildingStamp[]; __atlasBStampsVer?: number };
function stampStore(): BuildingStamp[] { return (G.__atlasBStamps ??= []); }
/** Every mutation of the store bumps this (also on globalThis — the instance-split law: a
 *  second module instance must see the first one's writes invalidate its index). */
function bumpStamps(): void { G.__atlasBStampsVer = (G.__atlasBStampsVer ?? 0) + 1; }
const BSTAMP_BEVEL = 2.0;

// ── THE ROOM FIT (interiors programme C2, 2026-10-01 — "the black band at the foot of the small house's back wall").
// THE STAMP WAS FINER THAN THE MESH. The stamp flattens the height FIELD in the building's rect, but the near terrain
// MESH samples that field every CHUNK_SIZE / 48 = 5.33 m (2.67 m on high / ultra). A deed on a slope is seated at its
// footprint's LOWEST point, so all of its natural ground stands above its floor; a 9.8 × 6.9 m house holds one or two
// lattice vertices in its flat, and the triangles from them to the next vertex UPHILL — outside the rect and its 2 m
// bevel, at its natural height — pass THROUGH the room: lit dirt above the unlit floor (a brown wedge by day, a black
// band at night) and the terrain trimesh under a body's feet. (verify:interior-ground: 49 cm over the floor in the
// probe-range house; 281 of 288 sloped placements.)
// The fit: out to ONE NEAR-RING QUAD beyond the rect (measured in the rect's own frame, so a diagonal house gets the
// quad's diagonal) ground higher than the floor is CUT to it — every vertex of every quad that overlaps the building
// is then at or under the floor, at seg 48 and at seg 96. Cut-only: the downhill side, the door and anything at or
// under the floor are untouched; a neighbour's own rect and bank are its own (the cut fades out over the bevel toward
// any OTHER stamp's rect). Deeds only — a settlement's buildings stand on a level plateau and register plain stamps.
/** One near-ring lattice quad (m) — `CHUNK_SIZE / CHUNK_SEG_NEAR` (256 / 48; config.ts is imported further down). */
const ROOM_FIT_QUAD_M = 256 / 48;
/** The stamp rect already reaches 0.8 m past the walls (every registrar adds it); the margin is the quad less that, +0.1. */
const ROOM_FIT_RECT_SLACK_M = 0.8;
export function roomFitMargin(yaw: number): number {
  return ROOM_FIT_QUAD_M * (Math.abs(Math.cos(yaw)) + Math.abs(Math.sin(yaw))) - ROOM_FIT_RECT_SLACK_M + 0.1;
}
/** The lever: `localStorage atlas:interior-ground-fit = 'off'` → deeds register today's plain stamp (read at registration). */
export function roomFitEnabled(read?: (k: string) => string | null): boolean {
  try {
    const get = read ?? ((k: string) => (typeof localStorage === 'undefined' ? null : localStorage.getItem(k)));
    return get('atlas:interior-ground-fit') !== 'off';
  } catch { return true; }
}
/** A stamp as the `atlas:terrain-restamp` handler should see it: the chunks the CUT touches rebuild too (the handler
 *  reaches `hypot(hw, hd) + 4`; the fit's own reach is the margin + the bevel). */
export function restampReach<T extends BuildingStamp>(s: T): T {
  return s.fit ? { ...s, hw: s.hw + s.fit, hd: s.hd + s.fit } : s;
}
/** While > 0 the fit pass is skipped (`withoutRoomFit`): a building is SEATED on the ground as it stood before anyone's
 *  cut, so the fit changes the ground and never a building — and never ratchets a row of houses down a hill. */
let fitSuspended = 0;

// ── The stamp GRID (2026-09-22). The store only grows through a session (deduped by pose:
// every enterable building of every settlement assembled so far — ~150 a city), and every
// stamped height query anywhere in the world used to walk ALL of it (20 µs a query at 4 500
// stamps; the query rate is hundreds a frame: 5 per terrainSeat per creature / remote /
// droid / bolt / escort, the versona seats, the proxies). Each stamp is filed in every
// STAMP_CELL_M cell its coarse-reject circle's AABB touches, in STORE ORDER, so a query
// visits only its own cell's candidates in the same relative order — the same winner, ties
// included (the NEAREST-wins loop keeps the first strictly-better stamp). Rebuilt lazily
// when the store's version moves. ──
const STAMP_CELL_M = 64;
const STAMP_CELL_BIAS = 1 << 20; // cell indices stay non-negative for the packed key (±67 000 km)
let gridVer = -1;
const stampGridCells = new Map<number, BuildingStamp[]>();
const stampCellKey = (cx: number, cz: number): number => (cx + STAMP_CELL_BIAS) * 2_097_152 + (cz + STAMP_CELL_BIAS);
/** The coarse-reject radius applyBuildingStamps tests (kept in one place so the grid and the loop agree). */
const stampReach = (s: BuildingStamp): number => s.hw + s.hd + BSTAMP_BEVEL + 2 + 2 * (s.fit ?? 0); // + the fit's cut (0 for a plain stamp: unchanged)
function stampGrid(): Map<number, BuildingStamp[]> {
  const ver = G.__atlasBStampsVer ?? 0;
  if (ver === gridVer) return stampGridCells;
  stampGridCells.clear();
  for (const s of stampStore()) {
    const rr = stampReach(s);
    const x0 = Math.floor((s.x - rr) / STAMP_CELL_M), x1 = Math.floor((s.x + rr) / STAMP_CELL_M);
    const z0 = Math.floor((s.z - rr) / STAMP_CELL_M), z1 = Math.floor((s.z + rr) / STAMP_CELL_M);
    for (let cx = x0; cx <= x1; cx++) {
      for (let cz = z0; cz <= z1; cz++) {
        const k = stampCellKey(cx, cz);
        const cell = stampGridCells.get(k);
        if (cell) cell.push(s); else stampGridCells.set(k, [s]);
      }
    }
  }
  gridVer = ver;
  return stampGridCells;
}

/** Append stamps (main thread, at settlement/deed assembly). Dedupes by pose. */
export function registerBuildingStamps(list: BuildingStamp[]): BuildingStamp[] {
  const store = stampStore();
  // Returns the stamps actually ADDED (bug sweep 2026-09-22): a re-assembled town re-registers stamps
  // the store already holds, and restamping the chunks for those tore the ground and its trees down
  // for nothing — the caller restamps only what is new.
  const added: BuildingStamp[] = [];
  for (const s of list) {
    if (!store.some((b) => Math.abs(b.x - s.x) < 0.5 && Math.abs(b.z - s.z) < 0.5)) { store.push(s); added.push(s); }
  }
  if (added.length > 0) bumpStamps();
  return added;
}

/** Remove the stamp(s) registered at (x, z) — a deed structure removed or a world hop.
 *  The store was append-only before: a deed's footprint kept flattening terrain in
 *  the NEXT world and the registry grew for the whole session. */
export function unregisterBuildingStamps(x: number, z: number): void {
  const store = stampStore();
  let removed = false;
  for (let i = store.length - 1; i >= 0; i--) {
    if (Math.abs(store[i].x - x) < 0.5 && Math.abs(store[i].z - z) < 0.5) { store.splice(i, 1); removed = true; }
  }
  if (removed) bumpStamps();
}

/** Drop every registered stamp (world hop). */
export function clearBuildingStamps(): void { stampStore().length = 0; bumpStamps(); }

/** Replace the whole set (worker: per-message payload owns the truth). */
export function setBuildingStamps(list: BuildingStamp[]): void {
  const store = stampStore();
  store.length = 0;
  store.push(...list);
  bumpStamps();
}

/** Stamps whose rect (+bevel) intersects an AABB (worker payload filter). */
export function buildingStampsNear(minX: number, minZ: number, maxX: number, maxZ: number): BuildingStamp[] {
  return stampStore().filter((s) => {
    const f = s.fit ?? 0;
    const r = Math.hypot(s.hw + f, s.hd + f) + BSTAMP_BEVEL; // the fit's cut reaches the worker too
    return s.x + r >= minX && s.x - r <= maxX && s.z + r >= minZ && s.z - r <= maxZ;
  });
}

function applyBuildingStamps(h: number, wx: number, wz: number): number {
  // NEAREST stamp wins (deepest rect containment): dense blocks overlap
  // rects, and sequential compose made the LAST building flatten its
  // neighbors' ground to ITS height (a tower centre read +4.8 m — the
  // neighbor's foundation). Each point belongs to the stamp it sits deepest
  // inside; the bevel still blends the winner's edge.
  let best: BuildingStamp | null = null;
  let bestSd = BSTAMP_BEVEL;
  let fitSeen = false;
  const cell = stampGrid().get(stampCellKey(Math.floor(wx / STAMP_CELL_M), Math.floor(wz / STAMP_CELL_M)));
  if (!cell) return h; // no stamp reaches this cell
  for (const s of cell) {
    const dx = wx - s.x, dz = wz - s.z;
    const rr = stampReach(s);
    if (dx * dx + dz * dz > rr * rr) continue; // coarse reject (hot path)
    if (s.fit) fitSeen = true;
    const c = Math.cos(s.yaw), sn = Math.sin(s.yaw);
    const lx = Math.abs(c * dx - sn * dz), lz = Math.abs(sn * dx + c * dz);
    const sd = Math.max(lx - s.hw, lz - s.hd); // rect SDF (outside-positive, box metric)
    if (sd < bestSd) { bestSd = sd; best = s; }
  }
  if (best) {
    const mask = 1 - smoothstep(0, BSTAMP_BEVEL, Math.max(0, bestSd));
    h = h + (best.h - h) * mask;
  }
  if (fitSeen && fitSuspended === 0) h = applyRoomFit(h, wx, wz, cell);
  return h;
}

/** THE ROOM FIT's pass (rare: only where a fitted stamp is in reach). Each fitted stamp cuts ground higher than its
 *  floor down to it inside rect + margin, easing out over the bevel — and stands back from every OTHER stamp's rect
 *  by the same bevel (a neighbour's pad and bank are its own: the cut never undermines the house next door). */
function applyRoomFit(h: number, wx: number, wz: number, cell: BuildingStamp[]): number {
  for (const s of cell) {
    const fit = s.fit;
    if (!fit || !(h > s.h)) continue; // cut-only: ground at or under this floor is never touched
    const dx = wx - s.x, dz = wz - s.z;
    const c = Math.cos(s.yaw), sn = Math.sin(s.yaw);
    const lx = Math.abs(c * dx - sn * dz), lz = Math.abs(sn * dx + c * dz);
    const sd = Math.max(lx - s.hw - fit, lz - s.hd - fit);
    if (!(sd < BSTAMP_BEVEL)) continue;
    let mask = 1 - smoothstep(0, BSTAMP_BEVEL, Math.max(0, sd));
    for (const o of cell) {
      if (o === s) continue;
      const ox = wx - o.x, oz = wz - o.z;
      const oc = Math.cos(o.yaw), osn = Math.sin(o.yaw);
      const osd = Math.max(Math.abs(oc * ox - osn * oz) - o.hw, Math.abs(osn * ox + oc * oz) - o.hd);
      if (osd < BSTAMP_BEVEL) mask *= smoothstep(0, BSTAMP_BEVEL, Math.max(0, osd));
      if (mask <= 0) break;
    }
    if (mask > 0) h = h + (s.h - h) * mask;
  }
  return h;
}

/** Run `fn` with every stamp's fit pass switched off (the deed's seat — see `fitSuspended`). */
export function withoutRoomFit<T>(fn: () => T): T {
  fitSuspended++;
  try { return fn(); } finally { fitSuspended--; }
}

/** The building-stamp pass alone (verify:stamp-grid's parity oracle compares it with the old full scan). */
export function applyBuildingStampsAt(h: number, wx: number, wz: number): number { return applyBuildingStamps(h, wx, wz); }

/** Terrain height with settlement roads + pads conformed in (world coords). */
export function stampedHeight(seed: string, wx: number, wz: number): number {
  return stampedHeightFrom(terrainHeight(seed, wx, wz), seed, wx, wz);
}

/** The conformed height given an already-evaluated terrain height (the batched
 *  kernel path hands the worker a whole chunk of raw heights at once). */
export function stampedHeightFrom(terrainH: number, seed: string, wx: number, wz: number): number {
  let h = terrainH;
  if (!seed) return h;
  for (const site of sitesFor(seed)) {
    // Gate on the DESIRED plan radius (plans now reach far beyond the reserved
    // footprint — gating on the footprint left everything past ~150 m of a
    // city center unconformed: floating roads, buried pads).
    const reach = PLAN_RADIUS_DESIRED[site.tier] + STAMP_MARGIN;
    const dx = wx - site.centerX;
    const dz = wz - site.centerZ;
    if (dx * dx + dz * dz > reach * reach) continue;
    // PEEK, never generate: the first movement-clamp/versona-seat sample at
    // spawn lands INSIDE the origin city before the bake worker can deliver its
    // plan, and a sync city-tier generation here froze the tab 5–13 s at join.
    // Un-primed sites stamp nothing (raw terrain) and self-correct once the
    // worker plan primes the memo / the built chunk registers its height grid.
    const plan = peekSettlementPlan(seed, site.siteSeed);
    if (plan) h = stampSite(h, dx, dz, plan);
    // Port APRONS stamp last (they out-rank roads/pads locally) and are
    // PLAN-INDEPENDENT — the shuttle-pad arrival ground is level from the
    // first spawn sample, before any worker plan primes. High-relief audit
    // 2026-08-05: terrain-blind port spots measured ±50 m float/burial.
    for (const ap of portAprons(seed, site)) {
      const d = Math.hypot(wx - ap.x, wz - ap.z);
      if (d < ap.r + ap.falloff) {
        const mask = 1 - smoothstep(ap.r, ap.r + ap.falloff, d);
        h = h + (ap.h - h) * mask;
      }
    }
  }
  // The Grand Circuit's corridor (phase 5): plan-independent, after site plans and aprons —
  // a level track + shoulder at the graded height, a causeway over any river it crosses.
  h = circuitHeight(h, seed, wx, wz);
  // Edit Mode sculpt strokes (raise/flatten) land on the conformed ground…
  h = applySculpt(h, wx, wz);
  // …and building stamps still out-rank everything: the ground under a placed
  // structure is FLAT at its foundation height.
  return applyBuildingStamps(h, wx, wz);
}

/**
 * How strongly a world point sits ON a settlement carriageway (0..1) — the
 * ROAD-SURFACE mask for the terrain material's track tint (2026-08-31 wave).
 * Tighter than the height corridor (halfWidth+2.2 vs +8): the ground reads a
 * worn route exactly where wheels run, and the stamped shoulder stays biome
 * ground. Peek-only, same self-heal law as stampedHeight.
 */
export function roadSurfaceMask(seed: string, wx: number, wz: number): number {
  if (!seed) return 0;
  // Regional route tracks (Unbuilt Town wave): the worn cross-country lines
  // between neighboring settlements — the same straight lines war clashes and
  // treaty stones already sit on. Plan-independent, so they read from the
  // very first chunk bake.
  let mask = Math.max(routeMask(seed, wx, wz), circuitMask(seed, wx, wz)); // the circuit's asphalt reads as a road (tint + flora rejection)
  for (const site of sitesFor(seed)) {
    const reach = PLAN_RADIUS_DESIRED[site.tier] + STAMP_MARGIN;
    const dx = wx - site.centerX;
    const dz = wz - site.centerZ;
    if (dx * dx + dz * dz > reach * reach) continue;
    const plan = peekSettlementPlan(seed, site.siteSeed);
    if (!plan) continue;
    const idx = indexFor(plan);
    const c = Math.floor((dx - idx.minX) / idx.cell);
    const r = Math.floor((dz - idx.minZ) / idx.cell);
    if (c < 0 || r < 0 || c >= idx.cols || r >= idx.rows) continue;
    for (const ei of idx.edges[r * idx.cols + c] ?? []) {
      const edge = plan.roads.edges[ei];
      const d = distanceToPolyline(dx, dz, edge.polyline);
      const m = 1 - smoothstep(Math.max(0.4, edge.halfWidth - 0.5), edge.halfWidth + 2.2, d.dist);
      if (m > mask) mask = m;
      if (mask >= 0.999) return 1;
    }
    // Desire lines: faint worn-grass tracks between the venues daily life
    // actually walks (weight 0.5 — lighter than any carriageway).
    for (const line of desireLinesFor(plan, seed, site.siteSeed)) {
      const d = distanceToPolyline(dx, dz, line);
      const m = (1 - smoothstep(0.7, 2.4, d.dist)) * 0.5;
      if (m > mask) mask = m;
    }
  }
  return mask;
}

/**
 * Project a point OUT of settlement building footprints + port hulls
 * (versona/NPC rendering — the server moves NPCs in geometry-blind 2D, so a
 * wander offset near an anchor can land INSIDE a tower; the client renders
 * the body on the nearest street instead). Returns null when already clear
 * (the overwhelmingly common case — one distance-square reject per site).
 *
 * CLEAR_M sits just outside the footprint: near the pad edge the stamp mask
 * is ≈1, so the pushed body still stands on flat pad-height ground rather
 * than mid-bevel (the graded-terrace walls are steepest mid-band).
 */
const CLEAR_M = 1.2;

export function settlementClearance(seed: string, wx: number, wz: number): { x: number; z: number } | null {
  let x = wx;
  let z = wz;
  let moved = false;
  for (const site of sitesFor(seed)) {
    const reach = PLAN_RADIUS_DESIRED[site.tier] + STAMP_MARGIN;
    const dx0 = x - site.centerX;
    const dz0 = z - site.centerZ;
    if (dx0 * dx0 + dz0 * dz0 > reach * reach) continue;
    const discs = portClearances(seed, site);
    const plan = peekSettlementPlan(seed, site.siteSeed);
    const idx = plan ? indexFor(plan) : null;
    if (discs.length === 0 && !idx) continue;

    // Deepest violation (pad SDF or port-disc) at a LOCAL point; sd < CLEAR_M
    // violates. Pads and discs share one field so a push out of one can never
    // be silently undone by the other (the pads-then-discs split parked
    // bodies back inside the shuttle-pad annulus).
    const worstAt = (lx: number, lz: number): { sd: number; pad: Float64Array | null; disc: { x: number; z: number; r: number } | null } => {
      let sd = CLEAR_M;
      let pad: Float64Array | null = null;
      let disc: { x: number; z: number; r: number } | null = null;
      if (idx && plan) {
        const c = Math.floor((lx - idx.minX) / idx.cell);
        const r = Math.floor((lz - idx.minZ) / idx.cell);
        if (c >= 0 && r >= 0 && c < idx.cols && r < idx.rows) {
          for (const pi of idx.pads[r * idx.cols + c]) {
            const s = signedDistanceToPolygon(lx, lz, plan.buildings[pi].footprint);
            if (s < sd) { sd = s; pad = plan.buildings[pi].footprint; disc = null; }
          }
        }
      }
      for (const pc of discs) {
        const s = Math.hypot(site.centerX + lx - pc.x, site.centerZ + lz - pc.z) - pc.r;
        if (s < sd) { sd = s; disc = pc; pad = null; }
      }
      return { sd, pad, disc };
    };

    let lx = x - site.centerX;
    let lz = z - site.centerZ;
    const startLx = lx;
    const startLz = lz;
    let w = worstAt(lx, lz);
    if (w.sd >= CLEAR_M) continue; // clear — the overwhelmingly common case
    // Greedy pushes out of the deepest violator. Overlapping/close-packed
    // footprints can ping-pong a gradient walk (push out of A lands in B,
    // B's gradient points back into A) — cap the walk and verify below.
    for (let pass = 0; pass < 8 && w.sd < CLEAR_M; pass++) {
      let ux = 1;
      let uz = 0;
      if (w.pad) {
        const e = 0.5;
        const gx = signedDistanceToPolygon(lx + e, lz, w.pad) - signedDistanceToPolygon(lx - e, lz, w.pad);
        const gz = signedDistanceToPolygon(lx, lz + e, w.pad) - signedDistanceToPolygon(lx, lz - e, w.pad);
        const gl = Math.hypot(gx, gz) || 1;
        ux = gx / gl;
        uz = gz / gl;
      } else if (w.disc) {
        const dx = site.centerX + lx - w.disc.x;
        const dz = site.centerZ + lz - w.disc.z;
        const d = Math.hypot(dx, dz);
        if (d > 1e-6) { ux = dx / d; uz = dz / d; }
      }
      lx += ux * (CLEAR_M - w.sd);
      lz += uz * (CLEAR_M - w.sd);
      w = worstAt(lx, lz);
    }
    if (w.sd < CLEAR_M * 0.4) {
      // Gradient walk trapped (overlapping footprints) — radial scan from the
      // ORIGINAL point: 16 directions × growing radii, first clear sample
      // wins (streets exist within ~10 m in every plan); else least-bad.
      let bestSd = w.sd;
      let bestLx = lx;
      let bestLz = lz;
      outer: for (let ring = 2; ring <= 30; ring += 2) {
        for (let k = 0; k < 16; k++) {
          const a = (k / 16) * Math.PI * 2;
          const sx = startLx + Math.cos(a) * ring;
          const sz = startLz + Math.sin(a) * ring;
          const s = worstAt(sx, sz);
          if (s.sd > bestSd) { bestSd = s.sd; bestLx = sx; bestLz = sz; }
          if (s.sd >= CLEAR_M * 0.9) break outer;
        }
      }
      lx = bestLx;
      lz = bestLz;
    }
    x = site.centerX + lx;
    z = site.centerZ + lz;
    moved = true;
  }
  return moved ? { x, z } : null;
}

/**
 * Warm every settlement plan one-per-macrotask so the first chunk build near a
 * city never pays plan generation mid-frame. Idempotent per seed.
 */
export function precacheSettlementPlans(seed: string): void {
  // Perf wave 3: main-thread plan warming is RETIRED. Even spaced
  // one-per-macrotask, a single city-tier plan is a 5–13 s long task — the
  // harness showed the warm sweep as the dominant hitch source at EVERY
  // site. Plans now come from the bake worker (CommonsSettlementsModule
  // requests them on approach and primes this thread's memo via
  // primeSettlementPlan); the chunk worker generates its own for stamping.
  // The rare main-side sync consumer (stampedHeight fallback at a streaming
  // edge with no Worker support) still generates lazily on demand.
  void seed;
  void warmed;
  void sitesFor;
}

// ─── Rendered-mesh height (what you SEE, not the analytic field) ───────────────
//
// The chunk streamer displaces a (seg+1)² vertex grid by stampedHeight and the
// GPU linearly interpolates between vertices — so on curved steep terrain the
// VISIBLE surface sags below the analytic field by up to ~1 m at the far LOD
// (256 m / 24 seg ≈ 10.7 m cells). Anything seated on the analytic height
// therefore floats mid-cell, worst near ridges — the "floating foliage" bug.
//
// meshHeight() reproduces the rendered surface EXACTLY: same grid, same corner
// heights, same PlaneGeometry anti-diagonal triangulation (faces (a,b,d)/(b,c,d)
// ⇒ the diagonal joins (x0,z1)-(x1,z0)), interpolated barycentrically. The
// chunk streamer registers each built chunk's seg here (near 48 / far 24 —
// chunks do NOT re-LOD on approach, so the registry is the only exact source).

import { CHUNK_SIZE, CHUNK_SEG_NEAR } from '../config.js';

// Keyed by a packed integer, not the "cx,cz" string: meshHeight is the hottest
// lookup in the client (5× per terrainSeat, per creature / remote / droid /
// bolt / escort per frame) and a template-string key allocated + hashed a
// fresh string on every call. The streamer's string keys are parsed ONCE at
// registration (rare). ±32768 chunks per axis covers the 2048 km shard 4×.
const chunkSegs = new Map<number, { seg: number; heights: Float32Array | null }>();
const chunkKeyOf = (cx: number, cz: number): number => (cx + 32768) * 65536 + (cz + 32768);
function parseChunkKey(key: string): number {
  const i = key.indexOf(',');
  return chunkKeyOf(Number(key.slice(0, i)), Number(key.slice(i + 1)));
}

/** Chunk streamer hook: record (or clear with null) a built chunk's segment count. */
export function setChunkSeg(key: string, seg: number | null): void {
  const k = parseChunkKey(key);
  if (seg === null) chunkSegs.delete(k);
  else chunkSegs.set(k, { seg, heights: chunkSegs.get(k)?.heights ?? null });
}

/**
 * Record a built chunk's RENDERED height grid (the exact per-vertex values
 * written to the mesh, row-major row·(seg+1)+col with row ↔ z). meshHeight
 * then interpolates the ARRAY instead of re-evaluating the analytic noise
 * stack (~1000× cheaper) — the movement clamp, versona seating, and foliage
 * scatter all ride this on every chunk the streamer has loaded.
 */
export function setChunkHeights(key: string, seg: number, heights: Float32Array): void {
  chunkSegs.set(parseChunkKey(key), { seg, heights });
}

/**
 * Height of the RENDERED terrain mesh at world (x, z) — grid-snapped corner
 * samples of stampedHeight, triangle-interpolated like the GPU does. Falls back
 * to the near-LOD grid when the chunk isn't built (streaming edge): still far
 * closer to the eventual mesh than the raw analytic sample.
 */
export function meshHeight(seed: string, x: number, z: number): number {
  const cx = Math.floor(x / CHUNK_SIZE);
  const cz = Math.floor(z / CHUNK_SIZE);
  const entry = chunkSegs.get(chunkKeyOf(cx, cz));
  const seg = entry?.seg ?? CHUNK_SEG_NEAR;
  const cell = CHUNK_SIZE / seg;
  const baseX = cx * CHUNK_SIZE;
  const baseZ = cz * CHUNK_SIZE;
  // Grid cell + intra-cell fractions (clamped: x/z exactly on the +edge belongs
  // to the last cell so corner sampling never crosses into the next chunk).
  let gx = (x - baseX) / cell;
  let gz = (z - baseZ) / cell;
  let ix = Math.floor(gx); if (ix >= seg) ix = seg - 1;
  let iz = Math.floor(gz); if (iz >= seg) iz = seg - 1;
  const u = gx - ix;
  const v = gz - iz;
  let hA: number, hB: number, hC: number, hD: number;
  const grid = entry?.heights;
  if (grid) {
    // FAST PATH: the rendered grid is cached — four array reads instead of
    // four full analytic evaluations (movement clamp / seating hot path).
    const side = seg + 1;
    hA = grid[iz * side + ix]; //       (ix,   iz)
    hB = grid[(iz + 1) * side + ix]; // (ix,   iz+1)
    hC = grid[(iz + 1) * side + ix + 1]; // (ix+1, iz+1)
    hD = grid[iz * side + ix + 1]; //   (ix+1, iz)
  } else {
    const x0 = baseX + ix * cell, x1 = x0 + cell;
    const z0 = baseZ + iz * cell, z1 = z0 + cell;
    hA = stampedHeight(seed, x0, z0);
    hB = stampedHeight(seed, x0, z1);
    hC = stampedHeight(seed, x1, z1);
    hD = stampedHeight(seed, x1, z0);
  }
  // PlaneGeometry faces: (a,b,d) covers u+v ≤ 1, (b,c,d) covers u+v ≥ 1.
  return u + v <= 1
    ? hA + (hD - hA) * u + (hB - hA) * v
    : hC + (hB - hC) * (1 - u) + (hD - hC) * (1 - v);
}

/** Does any FITTED stamp's reach cover this point? (the seat's guard — plain stamps never count) */
function roomFitReaches(wx: number, wz: number): boolean {
  const cell = stampGrid().get(stampCellKey(Math.floor(wx / STAMP_CELL_M), Math.floor(wz / STAMP_CELL_M)));
  if (!cell) return false;
  for (const s of cell) {
    if (!s.fit) continue;
    const dx = wx - s.x, dz = wz - s.z, rr = stampReach(s);
    if (dx * dx + dz * dz <= rr * rr) return true;
  }
  return false;
}

/**
 * `meshHeight` as it reads with NO stamp's room fit applied — what a building is SEATED on (THE ROOM FIT, C2
 * 2026-10-01: the fit changes the ground, never a building). Where no fitted stamp reaches the quad this IS
 * `meshHeight` (the resident grid, bit for bit — every seat that existed before the fit is unchanged); where one does,
 * the quad's four vertices are re-evaluated analytically with the fit pass off, so a house built beside another is
 * seated on the hill as it stood, not on its neighbour's cut pad (which would ratchet a row of houses down a slope).
 */
export function meshHeightUnfit(seed: string, x: number, z: number): number {
  const cx = Math.floor(x / CHUNK_SIZE), cz = Math.floor(z / CHUNK_SIZE);
  const seg = chunkSegs.get(chunkKeyOf(cx, cz))?.seg ?? CHUNK_SEG_NEAR;
  const cell = CHUNK_SIZE / seg;
  const gx = (x - cx * CHUNK_SIZE) / cell, gz = (z - cz * CHUNK_SIZE) / cell;
  let ix = Math.floor(gx); if (ix >= seg) ix = seg - 1;
  let iz = Math.floor(gz); if (iz >= seg) iz = seg - 1;
  const x0 = cx * CHUNK_SIZE + ix * cell, x1 = x0 + cell;
  const z0 = cz * CHUNK_SIZE + iz * cell, z1 = z0 + cell;
  if (!roomFitReaches(x0, z0) && !roomFitReaches(x0, z1) && !roomFitReaches(x1, z1) && !roomFitReaches(x1, z0)) return meshHeight(seed, x, z);
  const u = gx - ix, v = gz - iz;
  return withoutRoomFit(() => {
    const hA = stampedHeight(seed, x0, z0), hB = stampedHeight(seed, x0, z1), hC = stampedHeight(seed, x1, z1), hD = stampedHeight(seed, x1, z0);
    return u + v <= 1 ? hA + (hD - hA) * u + (hB - hA) * v : hC + (hB - hC) * (1 - u) + (hD - hC) * (1 - v);
  });
}
